import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Paper,
  FormControlLabel,
  Switch,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Collections as CollectionsIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Palette as PaletteIcon,
  CloudUpload as CloudUploadIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { io, Socket } from 'socket.io-client';
import { themeService, Theme, ThemeServiceContext } from '../../services/themeService';
import { SOCKET_URL } from '../../config/api';
import { toast } from 'react-toastify';
import { useI18n } from '../../i18n/useI18n';
import UserPageShell, { userCardEnterSx } from '../shared/UserPageShell';
import { getThemeDetailPath, ThemeChannel } from '../../utils/themePaths';
import UserChannelCustomFrames from '../User/UserChannelCustomFrames';
import ThemeAvailabilityDialog, {
  formatAvailabilityStamp,
  isInsideSchedule,
} from './ThemeAvailabilityDialog';

export interface ThemeListProps {
  channel?: ThemeChannel;
  embedded?: boolean;
  userId?: string;
}

const ThemeList: React.FC<ThemeListProps> = ({
  channel = 'pro',
  embedded = false,
  userId,
}) => {
  const { pages: t } = useI18n();
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const apiCtx: ThemeServiceContext = useMemo(
    () => (userId ? { adminUserId: userId, channel } : { channel }),
    [userId, channel],
  );
  const showProShowAll = channel === 'pro';
  const showFrameList = channel === 'pro' || channel === 'lite';

  const dialogPaperSx = {
    m: { xs: 1.5, sm: 2 },
    width: { xs: 'calc(100% - 24px)', sm: '100%' },
    maxHeight: { xs: '88vh', sm: '90vh' },
    borderRadius: 3.5,
    overflow: 'hidden',
    backgroundImage: `linear-gradient(180deg, ${alpha(muiTheme.palette.primary.main, 0.07)} 0%, #fff 30%)`,
  };

  const [themes, setThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTheme, setNewTheme] = useState({
    name: '',
    description: '',
    tags: '',
    themeImage: null as File | null,
  });
  const [frameListOpen, setFrameListOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<Theme | null>(null);
  const [togglingMobileUploadThemeId, setTogglingMobileUploadThemeId] = useState<string | null>(null);
  const [proShowAllCustomFrames, setProShowAllCustomFrames] = useState(true);
  const [togglingProShowAll, setTogglingProShowAll] = useState(false);
  const [availabilityTheme, setAvailabilityTheme] = useState<Theme | null>(null);

  useEffect(() => {
    loadThemes();
  }, [userId, channel]);

  useEffect(() => {
    if (userId) return;

    const socket: Socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      path: '/socket.io',
    });

    const joinUserRoom = () => {
      socket.emit('join-user-room');
    };

    socket.on('connect', joinUserRoom);

    socket.on('theme:active-changed', (data: { themeId?: string; isActive?: boolean }) => {
      const themeId = data?.themeId;
      if (!themeId || typeof data.isActive !== 'boolean') return;
      setThemes((prev) =>
        prev.map((t) => (t._id === themeId ? { ...t, isActive: data.isActive! } : t))
      );
    });

    socket.on('theme:content-changed', (data: { themeId?: string; changeType?: string }) => {
      const themeId = data?.themeId;
      if (!themeId) return;

      if (data.changeType === 'theme-deleted') {
        setThemes((prev) => prev.filter((t) => t._id !== themeId));
        return;
      }

      if (data.changeType === 'theme-created') {
        loadThemes();
      }
    });

    socket.on('theme:pro-show-all-changed', (data: { proShowAllCustomFrames?: boolean }) => {
      if (typeof data?.proShowAllCustomFrames === 'boolean') {
        setProShowAllCustomFrames(data.proShowAllCustomFrames);
      }
    });

    return () => {
      socket.emit('leave-user-room');
      socket.off('connect', joinUserRoom);
      socket.off('theme:active-changed');
      socket.off('theme:content-changed');
      socket.off('theme:pro-show-all-changed');
      socket.disconnect();
    };
  }, [userId]);

  const loadThemes = async () => {
    try {
      setLoading(true);
      const response = await themeService.getThemes({ channel }, apiCtx);
      setThemes(response.themes || []);
      if (showProShowAll && typeof response.proShowAllCustomFrames === 'boolean') {
        setProShowAllCustomFrames(response.proShowAllCustomFrames);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi tải danh sách themes');
      toast.error('Lỗi khi tải danh sách themes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLiteTheme = async () => {
    try {
      if (!newTheme.name.trim()) {
        toast.error('Tên theme là bắt buộc');
        return;
      }
      const themeData = {
        name: newTheme.name.trim(),
        description: newTheme.description.trim(),
        tags: newTheme.tags ? newTheme.tags.split(',').map(tag => tag.trim()) : [],
        themeImage: newTheme.themeImage || undefined,
        channel,
      };
      if (newTheme.themeImage) {
        await themeService.createThemeWithImage(themeData, apiCtx);
      } else {
        await themeService.createTheme(themeData, apiCtx);
      }
      toast.success('Tạo theme thành công');
      setCreateDialogOpen(false);
      setNewTheme({ name: '', description: '', tags: '', themeImage: null });
      loadThemes();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Lỗi khi tạo theme');
    }
  };

  const handleDeleteTheme = async () => {
    if (!selectedTheme) return;

    try {
      await themeService.deleteTheme(selectedTheme._id, apiCtx);
      toast.success('Xóa theme thành công');
      setDeleteDialogOpen(false);
      setSelectedTheme(null);
      loadThemes();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Lỗi khi xóa theme');
    }
  };

  const handleThemeClick = (theme: Theme) => {
    navigate(getThemeDetailPath(channel, theme._id, userId));
  };

  const handleDeleteClick = (theme: Theme, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedTheme(theme);
    setDeleteDialogOpen(true);
  };

  const handleMobileUploadToggle = async (theme: Theme, event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const nextEnabled = event.target.checked;
    setTogglingMobileUploadThemeId(theme._id);
    try {
      if (channel === 'lite') {
        await themeService.setMobileUploadLiteEnabled(theme._id, nextEnabled, apiCtx);
        setThemes((prev) =>
          prev.map((t) =>
            t._id === theme._id ? { ...t, mobileUploadLiteEnabled: nextEnabled } : t,
          ),
        );
      } else {
        await themeService.setMobileUploadProEnabled(theme._id, nextEnabled, apiCtx);
        setThemes((prev) =>
          prev.map((t) =>
            t._id === theme._id ? { ...t, mobileUploadProEnabled: nextEnabled } : t,
          ),
        );
      }
      toast.success(nextEnabled ? 'Đã bật theme cho mobile upload' : 'Đã tắt theme cho mobile upload');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Lỗi khi cập nhật mobile upload theme');
    } finally {
      setTogglingMobileUploadThemeId(null);
    }
  };

  const mobileUploadEnabled = (theme: Theme) =>
    channel === 'lite' ? theme.mobileUploadLiteEnabled === true : theme.mobileUploadProEnabled === true;

  const isThemeCardOn = (theme: Theme) =>
    channel === 'lite' ? mobileUploadEnabled(theme) : theme.isActive !== false;

  const availabilityCaption = (theme: Theme) => {
    const hasSchedule = theme.scheduleEnabled === true;
    const hasMachines = theme.machineFilterEnabled === true;
    if (!hasSchedule && !hasMachines) return '';
    const timeLabel = hasSchedule
      ? isInsideSchedule(theme)
        ? `${formatAvailabilityStamp(theme.availableFrom)} – ${formatAvailabilityStamp(theme.availableUntil)}`
        : t.themes.scheduleOutside
      : t.themes.scheduleAlways;
    const machineLabel = hasMachines
      ? t.themes.scheduleMachineCount.replace('{n}', String(theme.allowedMachineCodes?.length || 0))
      : t.themes.scheduleNoMachines;
    return `${timeLabel} · ${machineLabel}`;
  };

  const handleProShowAllToggle = async (nextValue: boolean) => {
    try {
      setTogglingProShowAll(true);
      await themeService.setProShowAllCustomFrames(nextValue, apiCtx);
      setProShowAllCustomFrames(nextValue);
      toast.success(
        nextValue
          ? 'Đã bật hiển thị toàn bộ custom frame (Pro)'
          : 'Đã tắt — Pro chỉ hiện layout theo theme đang bật'
      );
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Lỗi khi cập nhật cài đặt Pro');
    } finally {
      setTogglingProShowAll(false);
    }
  };

  const applyImageFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Chỉ được upload file ảnh');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Kích thước file không được vượt quá 10MB');
      return;
    }
    setNewTheme((prev) => ({ ...prev, themeImage: file }));
  };

  const content = (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          mb: { xs: 1.5, sm: embedded ? 1.5 : 2.5 },
        }}
      >
        {!embedded && (
          <Typography
            component="h1"
            fontWeight={800}
            sx={{
              fontSize: { xs: '1.25rem', sm: '1.85rem' },
              letterSpacing: '-0.03em',
              lineHeight: 1.2,
              minWidth: 0,
            }}
          >
            {t.themes.title}
          </Typography>
        )}
        {embedded && <Box sx={{ flex: 1 }} />}
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {showFrameList && (
            <Button
              variant="outlined"
              startIcon={!isMobile ? <CollectionsIcon /> : undefined}
              onClick={() => setFrameListOpen(true)}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 800,
                ...(isMobile ? { px: 1.25, minHeight: 40 } : { px: 2 }),
              }}
            >
              {t.themes.customFrameList}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={!isMobile ? <AddIcon /> : undefined}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              flexShrink: 0,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 800,
              boxShadow: 'none',
              ...(isMobile ? { minWidth: 40, width: 40, height: 40, p: 0 } : { px: 2 }),
            }}
            aria-label={t.themes.createNew}
          >
            {isMobile ? <AddIcon /> : t.themes.createNew}
          </Button>
        </Box>
      </Box>

      {showProShowAll && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.25, sm: 2 },
            mb: { xs: 1.5, sm: 2.5 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: alpha(muiTheme.palette.primary.main, 0.16),
            background: `linear-gradient(160deg, ${alpha(muiTheme.palette.primary.main, 0.08)} 0%, #fff 70%)`,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={proShowAllCustomFrames}
                onChange={(_, checked) => handleProShowAllToggle(checked)}
                disabled={togglingProShowAll}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
              />
            }
            label={
              <Box>
                <Typography fontWeight={800} sx={{ fontSize: { xs: '0.82rem', sm: '1rem' } }}>
                  {t.themes.proShowAllTitle}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, lineHeight: 1.35 }}
                >
                  {t.themes.proShowAllHint}
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
          />
        </Paper>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : themes.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <PaletteIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t.themes.emptyTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            {channel === 'lite' ? t.themes.emptyHintLite : t.themes.emptyHint}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
          >
            {t.themes.createNew}
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={{ xs: 1.25, sm: 2.5 }}>
          {themes.map((theme, index) => (
            <Grid item xs={6} sm={6} md={4} key={theme._id}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: isThemeCardOn(theme)
                    ? alpha(muiTheme.palette.primary.main, 0.28)
                    : 'divider',
                  overflow: 'hidden',
                  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                  ...userCardEnterSx(index),
                  '&:hover': {
                    transform: { sm: 'translateY(-2px)' },
                    boxShadow: `0 10px 24px ${alpha(muiTheme.palette.primary.main, 0.16)}`,
                  },
                }}
                onClick={() => handleThemeClick(theme)}
              >
                <CardMedia
                  sx={{
                    height: { xs: 112, sm: 168 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: alpha(muiTheme.palette.primary.main, 0.06),
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                  }}
                  image={theme.themeImage?.url}
                  title={theme.name}
                >
                  {!theme.themeImage && (
                    <ImageIcon sx={{ fontSize: { xs: 36, sm: 52 }, color: 'text.disabled' }} />
                  )}
                </CardMedia>
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, '&:last-child': { pb: 0 } }}>
                  <Typography
                    fontWeight={800}
                    noWrap
                    title={theme.name}
                    sx={{ fontSize: { xs: '0.85rem', sm: '1.05rem' }, mb: 0.5 }}
                  >
                    {theme.name}
                  </Typography>
                  {channel === 'lite' && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={mobileUploadEnabled(theme)}
                          onChange={(e) => handleMobileUploadToggle(theme, e)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={togglingMobileUploadThemeId === theme._id}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color={mobileUploadEnabled(theme) ? 'primary.main' : 'text.secondary'}
                          sx={{ fontSize: { xs: '0.68rem', sm: '0.75rem' }, lineHeight: 1.2 }}
                        >
                          {mobileUploadEnabled(theme) ? t.themes.active : t.themes.inactive}
                        </Typography>
                      }
                      title={t.themes.mobileUploadLiteHint}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ m: 0, justifyContent: 'center', gap: 0.25 }}
                    />
                  )}
                  <Box sx={{ mt: 0.25 }} onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setAvailabilityTheme(theme);
                      }}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                        borderRadius: 999,
                        fontSize: { xs: '0.68rem', sm: '0.75rem' },
                        minWidth: 0,
                        px: 1,
                      }}
                    >
                      {t.themes.scheduleOpen}
                    </Button>
                    {availabilityCaption(theme) && (
                      <Typography
                        variant="caption"
                        display="block"
                        color={
                          theme.scheduleEnabled && !isInsideSchedule(theme)
                            ? 'warning.main'
                            : 'text.secondary'
                        }
                        sx={{ fontSize: { xs: '0.62rem', sm: '0.7rem' }, lineHeight: 1.3, px: 0.5 }}
                      >
                        {availabilityCaption(theme)}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', px: { xs: 0.5, sm: 1.25 }, pb: { xs: 0.75, sm: 1.25 }, pt: 0 }}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => handleDeleteClick(theme, e)}
                    aria-label={t.common.delete}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {channel === 'pro' ? (
        <ThemeAvailabilityDialog
          mode="create"
          theme={null}
          open={createDialogOpen}
          channel={channel}
          apiCtx={apiCtx}
          userId={userId}
          onClose={() => setCreateDialogOpen(false)}
          onSaved={() => {
            setCreateDialogOpen(false);
            loadThemes();
          }}
        />
      ) : (
        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogTitle sx={{ fontWeight: 800, fontSize: { xs: '1.1rem', sm: '1.3rem' }, letterSpacing: '-0.02em', pt: 2.25, px: { xs: 2, sm: 3 } }}>
            {t.themes.dialogCreate}
          </DialogTitle>
          <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 1 }}>
            <Stack spacing={1.75} sx={{ mt: 0.5 }}>
              <TextField
                autoFocus
                fullWidth
                label={t.themes.name}
                required
                value={newTheme.name}
                onChange={(e) => setNewTheme({ ...newTheme, name: e.target.value })}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                label={t.themes.description}
                value={newTheme.description}
                onChange={(e) => setNewTheme({ ...newTheme, description: e.target.value })}
              />
              <TextField
                fullWidth
                label={t.themes.tags}
                placeholder="noel, christmas, 2024"
                value={newTheme.tags}
                onChange={(e) => setNewTheme({ ...newTheme, tags: e.target.value })}
              />
              <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}>
                {newTheme.themeImage ? newTheme.themeImage.name : t.themes.imageOptional}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) applyImageFile(file);
                    e.target.value = '';
                  }}
                />
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2, pt: 1.5, gap: 1 }}>
            <Button onClick={() => setCreateDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}>
              {t.common.cancel}
            </Button>
            <Button onClick={handleCreateLiteTheme} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 999, boxShadow: 'none' }}>
              {t.common.create}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Theme Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { ...dialogPaperSx, backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.15rem', pt: 2.25, px: { xs: 2, sm: 3 } }}>
          Xác nhận xóa
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Typography color="text.secondary">
            Bạn có chắc chắn muốn xóa theme "{selectedTheme?.name}"? Hành động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            fullWidth={isMobile}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
          >
            {t.common.cancel}
          </Button>
          <Button
            onClick={handleDeleteTheme}
            color="error"
            variant="contained"
            fullWidth={isMobile}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 999, boxShadow: 'none' }}
          >
            {t.common.delete}
          </Button>
        </DialogActions>
      </Dialog>

      <ThemeAvailabilityDialog
        theme={availabilityTheme}
        open={Boolean(availabilityTheme)}
        channel={channel}
        apiCtx={apiCtx}
        userId={userId}
        onClose={() => setAvailabilityTheme(null)}
        onSaved={(saved) => {
          setThemes((prev) => prev.map((item) => (item._id === saved._id ? { ...item, ...saved } : item)));
          setAvailabilityTheme(null);
          loadThemes();
        }}
      />

      <Dialog
        open={frameListOpen}
        onClose={() => setFrameListOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: { xs: '1.1rem', sm: '1.3rem' }, pt: 2.25, px: { xs: 2, sm: 3 } }}>
          {t.themes.customFrameList}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          {frameListOpen && (
            <UserChannelCustomFrames
              channel={channel}
              ownerUserId={userId}
              embedded
              showIntro={false}
              viewInDialog
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          <Button
            onClick={() => setFrameListOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
          >
            {t.common.close}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (embedded) {
    return content;
  }

  return <UserPageShell>{content}</UserPageShell>;
};

export default ThemeList;
